// Turning the Leads summary into something a business owner can read in a
// glance, without the screen deciding what any of it means.
//
// THE ONE RULE HERE: nothing in this file invents a number. Every count comes
// from the server; this only labels, groups and divides. That matters because
// the two halves of the summary come from DIFFERENT TABLES and are not
// commensurable:
//
//   • byOrigin / byQuality / bySync count LEADS      (agent_captures)
//   • byOutcome counts CALLS                         (agent_call_events)
//
// So "leads ÷ calls" is not a conversion rate — a single call can produce a
// lead and a booking, in-call captures are not represented in byOutcome at all,
// and 693 of the calls on this platform predate outcome recording entirely.
// Dividing the two would produce a percentage that looks authoritative and
// means nothing, which is worse than showing neither. The percentages below are
// therefore always WITHIN one of the two families, never across them.
import type { LeadOverview } from 'api/services/captures.service';

/** How a bucket should read on screen — the tone is what it means, not how big it is. */
export type Tone = 'good' | 'ok' | 'warn' | 'bad' | 'neutral';

export interface Segment {
  key: string;
  label: string;
  /** One line explaining the bucket, for a tooltip. Absent for buckets we invented. */
  hint?: string;
  count: number;
  /** Share of the group this segment belongs to, 0–100. 0 when the group is empty. */
  pct: number;
  tone: Tone;
}

/** Percent of a whole, safe at zero and never NaN on screen. */
export const share = (n: number, of: number): number => (of > 0 ? (n / of) * 100 : 0);

const ORIGIN_META: Record<string, { label: string; hint: string; tone: Tone }> = {
  IN_CALL: {
    label: 'Captured on the call',
    hint: 'The agent took the details down while the caller was on the line.',
    tone: 'good',
  },
  WEB: {
    label: 'From a web form',
    hint: 'A website form, not a call. No outcome, transcript or recording exists for these.',
    tone: 'ok',
  },
  POST_CALL: {
    label: 'Recovered afterwards',
    // Stated plainly because the number being the larger one surprises people,
    // and reading it as a failure is the wrong conclusion: a recovered lead is
    // a lead that would otherwise have been lost entirely.
    hint: 'Rebuilt from the transcript after the call ended, because the agent did not capture one live.',
    tone: 'ok',
  },
};

const QUALITY_META: Record<string, { label: string; tone: Tone }> = {
  High: { label: 'High', tone: 'good' },
  Medium: { label: 'Medium', tone: 'ok' },
  Low: { label: 'Low', tone: 'warn' },
  'Not set': { label: 'Not rated', tone: 'neutral' },
};

const SYNC_META: Record<string, { label: string; hint: string; tone: Tone }> = {
  SYNCED: { label: 'In your CRM', hint: 'Written to the connected CRM.', tone: 'good' },
  QUEUED: { label: 'Retrying', hint: 'The push failed and is scheduled to try again.', tone: 'warn' },
  FAILED: { label: 'Failed', hint: 'The push failed and will not be retried.', tone: 'bad' },
  // The CRM was never asked. The lead is missing a field its own agent declared
  // mandatory, so this is a lead problem, not an integration one — warn, not bad.
  INCOMPLETE: {
    label: 'Missing required fields',
    hint: 'Held back: the lead does not carry a field the agent declared mandatory. Nothing was sent to the CRM.',
    tone: 'warn',
  },
  // Both of these are SETTINGS, and showing them as failures is the single
  // easiest way to make this screen lie. One business deliberately runs with no
  // CRM at all; 341 of its leads sit in NOT_CONFIGURED and every one of them is
  // exactly where it is supposed to be.
  LOCAL_ONLY: {
    label: 'Kept here by choice',
    hint: 'A CRM is connected, and this agent is deliberately configured not to push to it.',
    tone: 'neutral',
  },
  NOT_CONFIGURED: {
    label: 'No CRM connected',
    hint: 'Nothing to push to — these leads live in cpass. Not a failure.',
    tone: 'neutral',
  },
};

const OUTCOME_META: Record<string, { label: string; hint: string; tone: Tone }> = {
  NO_CONVERSATION: {
    label: 'Nothing was said',
    hint: 'The call connected and no words were transcribed at either end.',
    tone: 'bad',
  },
  GREETING_ONLY: {
    label: 'Only our greeting',
    hint: 'The agent spoke; the caller never answered back.',
    tone: 'bad',
  },
  CONVERSATION: {
    label: 'A conversation',
    hint: 'They talked to us. Nothing has been extracted from it yet.',
    tone: 'ok',
  },
  LEAD_RECOVERED: {
    label: 'A lead came out of it',
    hint: 'We read the transcript afterwards and built a lead from it.',
    tone: 'good',
  },
  NO_LEAD_FOUND: {
    label: 'No lead in it',
    hint: 'They talked to us, and nothing in what they said was a lead.',
    tone: 'warn',
  },
  'Not classified': {
    label: 'Not classified',
    // Unmeasured, NOT empty. These calls happened; nobody recorded how they
    // ended, because they predate the pipeline that records it. Filing them
    // under "nothing was said" would invent a finding.
    hint: 'From before we started recording how a call ended. Unmeasured, not empty.',
    tone: 'neutral',
  },
};

/**
 * A map of counts as ordered segments.
 *
 * The SERVER's key order is kept — it zero-fills and orders every map, and a
 * second opinion about the order here is a second thing to keep in step. A key
 * this file has no label for still renders, under its own name: the alternative
 * is a bucket silently vanishing from a bar that still claims to be a whole.
 */
const segmentsOf = (
  counts: Record<string, number>,
  meta: Record<string, { label: string; hint?: string; tone: Tone }>,
): Segment[] => {
  const entries = Object.entries(counts || {});
  const total = entries.reduce((n, [, v]) => n + (v || 0), 0);
  return entries.map(([key, count]) => ({
    key,
    label: meta[key]?.label ?? key,
    hint: meta[key]?.hint,
    count: count || 0,
    pct: share(count || 0, total),
    tone: meta[key]?.tone ?? 'neutral',
  }));
};

export const originSegments = (o: LeadOverview): Segment[] => segmentsOf(o.byOrigin, ORIGIN_META);
export const qualitySegments = (o: LeadOverview): Segment[] => segmentsOf(o.byQuality, QUALITY_META);
export const syncSegments = (o: LeadOverview): Segment[] => segmentsOf(o.bySync, SYNC_META);

/**
 * The call outcomes, with "Not classified" held out of the shares.
 *
 * It is the largest bucket on this platform by an order of magnitude — 693
 * against 61 — and leaving it in the denominator makes every real outcome round
 * to 1% and the bar unreadable. It is not dropped: `unclassified` carries it,
 * and the screen states it beside the bar rather than inside it.
 */
export const outcomeSegments = (o: LeadOverview): Segment[] => {
  const counts = { ...(o.byOutcome || {}) };
  delete counts['Not classified'];
  return segmentsOf(counts, OUTCOME_META);
};

export const unclassifiedCalls = (o: LeadOverview): number => (o.byOutcome || {})['Not classified'] || 0;

const sum = (counts: Record<string, number>, keys: string[]): number =>
  keys.reduce((n, k) => n + ((counts || {})[k] || 0), 0);

/**
 * The one thing worth putting in a sentence.
 *
 * Of the calls we DID classify, how many never got a word out of the caller.
 * Both halves are calls, both from the same table, so the division is honest —
 * and it is the finding a business can act on: a high number is a telephony or
 * greeting problem, not a prompt problem.
 */
export interface SilenceRate {
  /** NO_CONVERSATION + GREETING_ONLY. */
  silent: number;
  /** Every call with a recorded outcome. */
  classified: number;
  pct: number;
}

export const silenceRate = (o: LeadOverview): SilenceRate => {
  const counts = o.byOutcome || {};
  const silent = sum(counts, ['NO_CONVERSATION', 'GREETING_ONLY']);
  const classified = Object.entries(counts)
    .filter(([k]) => k !== 'Not classified')
    .reduce((n, [, v]) => n + (v || 0), 0);
  return { silent, classified, pct: share(silent, classified) };
};

/** Leads the agent judged worth chasing. High only — "Medium" is not a promise. */
export const highQuality = (o: LeadOverview): number => (o.byQuality || {}).High || 0;

/**
 * Leads whose CRM copy needs a human.
 *
 * QUEUED, FAILED and INCOMPLETE. LOCAL_ONLY and NOT_CONFIGURED are settings and
 * counting them here would put a permanent red number on a business that is
 * working exactly as configured.
 *
 * INCOMPLETE belongs here even though nothing is wrong with the integration:
 * somebody has to decide whether to complete the lead or relax what the agent
 * requires, and until they do it is a lead the business paid for and cannot
 * see in its CRM.
 */
export const syncNeedsAttention = (o: LeadOverview): number =>
  sum(o.bySync, ['QUEUED', 'FAILED', 'INCOMPLETE']);

/** Whole numbers with thousands separators; these are counts, never decimals. */
export const count = (n: number): string => new Intl.NumberFormat().format(Math.round(n || 0));

/** A percentage a person reads, not a float. Kept to one place under 10%. */
export const percent = (n: number): string => `${n >= 10 || n === 0 ? Math.round(n) : n.toFixed(1)}%`;
